import { useEffect, useRef } from 'react';
import countingService from '../services/countingService';
import { isCountTaskCancelled } from './countTaskStatus';
import useWmsCountingSignalR from './useWmsCountingSignalR';
import type { CountTaskCancelNotification } from '../services/wmsCountingSignalRService';

/**
 * Sahadaki sayım akışı sırasında görevin iptal edilip edilmediğini izler.
 * SignalR ile anlık bildirim alır; bağlantı yoksa HTTP polling'e düşer.
 */
export default function useCountTaskCancellationWatch(
  taskId: string | undefined,
  warehouseId: string | undefined,
  enabled: boolean,
  onCancelled: (taskNumber: string, taskName: string) => void,
  intervalMs = 15000,
) {
  const onCancelledRef = useRef(onCancelled);
  onCancelledRef.current = onCancelled;

  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
  }, [taskId]);

  const handleSignalCancelled = (payload: CountTaskCancelNotification) => {
    if (firedRef.current || !taskId || payload.taskId !== taskId) return;
    firedRef.current = true;
    onCancelledRef.current(payload.taskNumber, payload.taskName);
  };

  useWmsCountingSignalR({
    warehouseId,
    enabled: enabled && !!taskId && !!warehouseId,
    onTaskCancelled: handleSignalCancelled,
  });

  useEffect(() => {
    if (!enabled || !taskId) return;

    let active = true;
    const check = async () => {
      if (firedRef.current) return;
      try {
        const detail = await countingService.getTaskDetail(taskId);
        if (!active || firedRef.current) return;
        if (isCountTaskCancelled(detail.status)) {
          firedRef.current = true;
          onCancelledRef.current(detail.taskNumber, detail.taskName);
        }
      } catch { /* sessiz — bir sonraki poll'da tekrar dener */ }
    };

    check();
    const timer = setInterval(check, intervalMs);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [taskId, enabled, intervalMs]);
}
